import {
  ApiError,
  type Account,
  type ApplicationTracker,
  type CodeReview,
  type DecideResult,
  type DecisionPriorities,
  type StudyPack,
  type StudyResult,
  type TrackedApplication,
  type ManualApplication,
  type TrackerUpdate,
  type AgentRun,
  type AgentStatus,
  type ApiErrorBody,
  type AnswerRequest,
  type AnswerResult,
  type ApprovalResult,
  type AppSettings,
  type ChatRequest,
  type ChatStreamEvent,
  type DocumentDraft,
  type DraftRequest,
  type Erasure,
  type FeedbackSummary,
  type FitResult,
  type FunnelCounts,
  type HealthResponse,
  type IndexResult,
  type InterviewOptions,
  type InterviewQuestion,
  type InterviewSession,
  type JobDetail,
  type JobFilters,
  type JobSummary,
  type OperatorFeedback,
  type OperatorLimits,
  type OperatorLimitsUpdate,
  type OperatorOverview,
  type PasteJobRequest,
  type Rating,
  type RateRequest,
  type MemoryFact,
  type MemoryPreview,
  type RememberRequest,
  type StartInterviewRequest,
  type VoiceTicket,
  type SyncRequest,
  type SettingsUpdate,
  type SyncResult,
  type ResumeSlot,
  type ResumeUpload,
  type Profile,
  type RolesSummary,
  type TargetRole,
  type SavedDocument,
  type UsageSummary,
} from "@/lib/types"
import { accessToken, reportUnauthenticated } from "@/lib/auth"

/**
 * Same-origin by construction: in production FastAPI serves this bundle, and in
 * development Vite proxies /api to uvicorn. There is no base-URL setting to get
 * wrong and no CORS to configure.
 */
const BASE = "/api"

/** The sign-in token for this request, when there is one. */
async function authHeader(): Promise<Record<string, string>> {
  const token = await accessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(await authHeader()),
        ...(init?.headers ?? {}),
      },
    })
  } catch {
    throw new ApiError(0, {
      code: "upstream_unavailable",
      message: "Could not reach the Praxa API. Is the server running?",
    })
  }

  if (response.status === 401) reportUnauthenticated()

  if (!response.ok) {
    let body: ApiErrorBody = {
      code: "internal_error",
      message: `Request failed with status ${response.status}.`,
    }
    try {
      const parsed = (await response.json()) as Partial<ApiErrorBody>
      if (parsed && typeof parsed.code === "string" && typeof parsed.message === "string") {
        body = parsed as ApiErrorBody
      }
    } catch {
      // A non-JSON error body is itself the useful signal; keep the default.
    }
    throw new ApiError(response.status, body)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * A file download that needs the sign-in header. A plain link cannot carry
 * it, so the file is fetched like any other call and saved from a blob URL.
 */
async function downloadFile(path: string): Promise<void> {
  const response = await fetch(`${BASE}${path}`, { headers: await authHeader() })
  if (response.status === 401) reportUnauthenticated()
  if (!response.ok) {
    throw new ApiError(response.status, {
      code: "internal_error",
      message: "That file could not be downloaded.",
    })
  }
  const disposition = response.headers.get("content-disposition") ?? ""
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "document"
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement("a")
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}

/* -------------------------------------------------------------------- chat */

/**
 * Opens the SSE stream for one chat turn.
 *
 * EventSource cannot POST, so the turn is created with a POST that returns a
 * stream id, and the stream is then read from a GET. `onEvent` receives every
 * parsed event in order; the returned function aborts the stream.
 */
export function streamChat(
  body: ChatRequest,
  onEvent: (event: ChatStreamEvent) => void,
): () => void {
  const controller = new AbortController()

  void (async () => {
    try {
      const response = await fetch(`${BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      if (response.status === 401) {
        reportUnauthenticated()
        onEvent({ type: "error", code: "unauthenticated", message: "Please sign in to continue." })
        return
      }

      if (!response.ok || !response.body) {
        onEvent({
          type: "error",
          code: "upstream_unavailable",
          message: "The API did not return a stream.",
        })
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // SSE frames are separated by a blank line.
        let split = buffer.indexOf("\n\n")
        while (split !== -1) {
          const frame = buffer.slice(0, split)
          buffer = buffer.slice(split + 2)
          const payload = frame
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trim())
            .join("")
          if (payload) {
            try {
              onEvent(JSON.parse(payload) as ChatStreamEvent)
            } catch {
              // A malformed frame should not kill the stream.
            }
          }
          split = buffer.indexOf("\n\n")
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return
      onEvent({
        type: "error",
        code: "upstream_unavailable",
        message: "The connection to the API was lost.",
      })
    }
  })()

  return () => controller.abort()
}

/* ------------------------------------------------------------------ routes */

export const api = {
  usage: (threadId?: string) =>
    request<UsageSummary>(
      threadId ? `/usage?threadId=${encodeURIComponent(threadId)}` : "/usage",
    ),

  health: () => request<HealthResponse>("/health"),

  /** Developer settings. Process-wide, and reverts on restart. */
  settings: {
    read: () => request<AppSettings>("/settings"),
    update: (body: SettingsUpdate) =>
      request<AppSettings>("/settings", { method: "POST", body: JSON.stringify(body) }),
  },

  /**
   * Ratings the user gave individual answers. Nothing here feeds back into how
   * the next answer is produced - see `routes/feedback.py` for why that is a
   * separate piece of work rather than an omission.
   */
  feedback: {
    read: (params?: { rating?: Rating; limit?: number }) => {
      const search = new URLSearchParams()
      if (params?.rating) search.set("rating", params.rating)
      if (params?.limit) search.set("limit", String(params.limit))
      const query = search.toString()
      return request<FeedbackSummary>(`/feedback${query ? `?${query}` : ""}`)
    },
    rate: (body: RateRequest) =>
      request<unknown>("/feedback", { method: "POST", body: JSON.stringify(body) }),
    unrate: (messageId: string) =>
      request<unknown>(`/feedback/${encodeURIComponent(messageId)}`, { method: "DELETE" }),
    clear: () => request<{ removed: number }>("/feedback", { method: "DELETE" }),
  },

  jobs: {
    list: (filters?: JobFilters) => {
      const search = new URLSearchParams()
      for (const [key, value] of Object.entries(filters ?? {})) {
        if (value !== undefined && value !== "") search.set(key, String(value))
      }
      const qs = search.toString()
      return request<JobSummary[]>(`/jobs${qs ? `?${qs}` : ""}`)
    },
    get: (id: string) => request<JobDetail>(`/jobs/${encodeURIComponent(id)}`),
    funnel: () => request<FunnelCounts>("/jobs/funnel"),
    /** Costs a model call and takes seconds. The caller shows a pending state. */
    score: (id: string) =>
      request<FitResult>(`/jobs/${encodeURIComponent(id)}/score`, { method: "POST" }),
    /** Delete one saved posting. Interviews and documents made for it are kept. */
    remove: (id: string) => request<void>(`/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),
    setStatus: (id: string, status: string, note?: string) =>
      request<JobDetail>(`/jobs/${encodeURIComponent(id)}/status`, {
        method: "POST",
        body: JSON.stringify({ status, note }),
      }),
    sources: () => request<string[]>("/jobs/sources"),
    /** Pull live postings from the job boards. Slow – it calls someone else's API. */
    sync: (body: SyncRequest = {}) =>
      request<SyncResult>("/jobs/sync", { method: "POST", body: JSON.stringify(body) }),
    paste: (posting: PasteJobRequest) =>
      request<JobDetail>("/jobs/paste", {
        method: "POST",
        body: JSON.stringify(posting),
      }),
  },

  /**
   * The application tracker and "Should I apply?". Everything is SQL and free
   * except `decide`, which may read the posting's facts and score its fit
   * once (model calls), then keeps both.
   */
  applications: {
    list: () => request<ApplicationTracker>("/applications"),
    /** Track an application made outside PRAXA. Free: no model call. */
    add: (application: ManualApplication) =>
      request<TrackedApplication>("/applications", {
        method: "POST",
        body: JSON.stringify(application),
      }),
    update: (jobId: string, changes: TrackerUpdate) =>
      request<TrackedApplication>(`/applications/${encodeURIComponent(jobId)}`, {
        method: "PATCH",
        body: JSON.stringify(changes),
      }),
    untrack: (jobId: string) =>
      request<void>(`/applications/${encodeURIComponent(jobId)}`, { method: "DELETE" }),
    decide: (jobId: string, refresh = false) =>
      request<DecideResult>(
        `/applications/${encodeURIComponent(jobId)}/decide${refresh ? "?refresh=true" : ""}`,
        { method: "POST" },
      ),
    /** The stored study pack, or a 404 when none was made yet. Free. */
    study: (jobId: string) =>
      request<StudyPack>(`/applications/${encodeURIComponent(jobId)}/study`),
    /** Build or rebuild the study pack: two model calls. */
    buildStudy: (jobId: string) =>
      request<StudyPack>(`/applications/${encodeURIComponent(jobId)}/study`, { method: "POST" }),
    answerStudy: (jobId: string, answers: (number | null)[]) =>
      request<StudyResult>(`/applications/${encodeURIComponent(jobId)}/study/answers`, {
        method: "POST",
        body: JSON.stringify({ answers }),
      }),
    priorities: () => request<DecisionPriorities>("/applications/priorities"),
    setPriorities: (priorities: DecisionPriorities) =>
      request<DecisionPriorities>("/applications/priorities", {
        method: "PUT",
        body: JSON.stringify(priorities),
      }),
  },

  /**
   * Generating a document and saving it are two calls on purpose: nothing is
   * kept until `approve`. Kept documents live in the account and come back as
   * a download. See `api/routes/documents.py`.
   */
  documents: {
    saved: () => request<SavedDocument[]>("/documents/saved"),
    removeSaved: (id: string) =>
      request<void>(`/documents/saved/${encodeURIComponent(id)}`, { method: "DELETE" }),
    /** Fetch a kept document with the sign-in header, then hand it to the browser as a file. */
    download: (id: string, format: "docx" | "md") =>
      downloadFile(`/documents/saved/${encodeURIComponent(id)}/download?format=${format}`),
    pending: () => request<DocumentDraft[]>("/documents/drafts"),
    draft: (body: DraftRequest) =>
      request<DocumentDraft>("/documents/draft", { method: "POST", body: JSON.stringify(body) }),
    approve: (id: string) =>
      request<ApprovalResult>(`/documents/${encodeURIComponent(id)}/approve`, { method: "POST" }),
    discard: (id: string) =>
      request<void>(`/documents/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  /**
   * Replacing a CV rebuilds the whole index, so these calls are slow on
   * purpose and the UI says so rather than pretending otherwise.
   */
  roles: {
    list: () => request<RolesSummary>("/roles"),
    add: (title: string, profile?: Profile) =>
      request<TargetRole>("/roles", {
        method: "POST",
        body: JSON.stringify(profile ? { title, profile } : { title }),
      }),
    remove: (id: string) => request<void>(`/roles/${encodeURIComponent(id)}`, { method: "DELETE" }),
    suggest: (title: string) =>
      request<{ profile: Profile; packLabel: string }>(
        `/roles/suggest?title=${encodeURIComponent(title)}`,
      ),
  },

  resumes: {
    list: () => request<ResumeSlot[]>("/resumes"),
    upload: (body: ResumeUpload) =>
      request<IndexResult>("/resumes", { method: "POST", body: JSON.stringify(body) }),
    remove: (profile: string) =>
      request<IndexResult>(`/resumes/${encodeURIComponent(profile)}`, { method: "DELETE" }),
    /** One CV off the General shelf, addressed by its own id rather than by track. */
    removeGeneral: (id: string) =>
      request<IndexResult>(`/resumes/general/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  /**
   * Scoring and asking are two calls on purpose. Doing both in one request
   * meant several seconds of nothing before anything appeared; split, the
   * score - the part the candidate is waiting for - arrives first and the next
   * question loads behind it.
   */
  /** The daily job agent: today's report, the switch, and Run now. */
  agent: {
    status: () => request<AgentStatus>("/agent"),
    setEnabled: (enabled: boolean) =>
      request<AgentStatus>("/agent/settings", {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      }),
    run: () => request<AgentRun>("/agent/run", { method: "POST" }),
  },
  interview: {
    options: () => request<InterviewOptions>("/interview/options"),
    /** Delete every practised interview. The progress figures start again. */
    clearHistory: () => request<{ removed: number }>("/interview/sessions", { method: "DELETE" }),
    start: (body: StartInterviewRequest) =>
      request<InterviewSession>("/interview/sessions", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /** Rebuild a session from the stored rows, after a refresh or in a new tab. */
    resume: (id: string) =>
      request<InterviewSession>(`/interview/sessions/${encodeURIComponent(id)}`),
    answer: (id: string, body: AnswerRequest) =>
      request<AnswerResult>(`/interview/sessions/${encodeURIComponent(id)}/answers`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /** A one-use token for answering the question on screen out loud. */
    voice: (id: string, question: string) =>
      request<VoiceTicket>(`/interview/sessions/${encodeURIComponent(id)}/voice`, {
        method: "POST",
        body: JSON.stringify({ question }),
      }),
    next: (id: string) =>
      request<InterviewQuestion>(`/interview/sessions/${encodeURIComponent(id)}/next`, {
        method: "POST",
      }),
  },

  /** Review a snippet for security, performance and readability. One model call. */
  codeReview: (code: string, language?: string) =>
    request<CodeReview>("/code-review", {
      method: "POST",
      body: JSON.stringify({ code, language: language || undefined }),
    }),

  /** The signed-in account: who it is, its data to take away, and erasure. */
  account: {
    me: () => request<Account>("/account"),
    /** Everything the account owns, as one JSON file (GDPR Art. 20). */
    download: () => downloadFile("/account/export"),
    /** Deletes everything, sign-in included. The body is the word the person typed. */
    erase: () =>
      request<Erasure>("/account", { method: "DELETE", body: JSON.stringify({ confirm: "DELETE" }) }),
  },

  /** The operator page. Every call is refused with 403 for anyone else. */
  operator: {
    overview: () => request<OperatorOverview>("/operator/overview"),
    feedback: () => request<OperatorFeedback>("/operator/feedback"),
    setLimits: (body: OperatorLimitsUpdate) =>
      request<OperatorLimits>("/operator/limits", { method: "PUT", body: JSON.stringify(body) }),
  },

  memory: {
    list: () => request<MemoryFact[]>("/memory"),
    /** The prompt block memory will contribute, straight from `recall_block`. */
    preview: () => request<MemoryPreview>("/memory/preview"),
    /** Stated outright by the user, which the server records as `stated`. */
    add: (body: RememberRequest) =>
      request<MemoryFact>("/memory", { method: "POST", body: JSON.stringify(body) }),
    remove: (id: number) => request<void>(`/memory/${id}`, { method: "DELETE" }),
    clear: () => request<{ removed: number }>("/memory", { method: "DELETE" }),
  },
}
